import { generateKek, type PlatformKeyStore } from '@signbento/crypto'
import type { IDBPDatabase } from 'idb'
import { StorageError } from '../errors.js'
import type { VaultDbSchema } from './idb-record-store.js'

/**
 * Web key store: the KEK is a non-extractable AES-KW `CryptoKey` held in
 * IndexedDB. It never leaves the platform in extractable form, and only the
 * wrapped DEK is persisted alongside it (PRD §11.2, §14.1).
 */
export class WebPlatformKeyStore implements PlatformKeyStore {
  constructor(private readonly db: IDBPDatabase<VaultDbSchema>) {}

  async getOrCreateKek(keyId: string): Promise<CryptoKey> {
    const metaKey = `kek:${keyId}`
    const existing = await this.db.get('meta', metaKey)
    if (existing instanceof CryptoKey) return existing
    if (existing !== undefined && existing !== null) {
      throw new StorageError('OP_STORAGE_CORRUPT_REVISION', `platform key ${keyId} is malformed`)
    }
    const kek = await generateKek()
    try {
      // `add` is an atomic first-writer-wins insert across tabs. A second opener must use the
      // winner's key; replacing it would strand a DEK already wrapped by that key.
      await this.db.add('meta', kek, metaKey)
      return kek
    } catch (error) {
      if (!(error instanceof Error) || error.name !== 'ConstraintError') throw error
      const winner = await this.db.get('meta', metaKey)
      if (winner instanceof CryptoKey) return winner
      throw new StorageError('OP_STORAGE_CORRUPT_REVISION', `platform key ${keyId} is malformed`)
    }
  }

  async deleteKek(keyId: string): Promise<void> {
    await this.db.delete('meta', `kek:${keyId}`)
  }
}
